import { useCallback, useEffect, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';
import { useUserContext } from './UserContext';
import { INITIAL_BUTTON_BACKGROUND, INITIAL_TEXT_COLORS } from '../constants/callToActions';
import { RGBAToHexA } from '../utils/CallToActionHelper';
import { useHistory, useLocation } from 'react-router-dom';

function useCallToAction() {
  const [isCallToActionOpened, setIsCallToActionOpened] = useState(false);

  const [callActions, setCallActions] = useState(null);
  const [initialCallActions, setInitialCallActions] = useState([]);

  const [selectedCallAction, setSelectedCallAction] = useState({});
  const [callActionRangeChanged, setCallActionRangeChanged] = useState(false);

  const [callActionsSeekValue, setCallActionsSeekValue] = useState(0);

  const [isCallActionsChanged, setIsCallActionsChanged] = useState(false);
  const [isNewCallToAction, setIsNewCallToAction] = useState(false);

  const [isNewActionTimeline, setIsNewActionTimeline] = useState(false); // immediately show CTA button if video is paused

  const [availableTextColors, setAvailableTextColors] = useState(INITIAL_TEXT_COLORS);
  const [availableButtonColors, setAvailableButtonColors] = useState(INITIAL_BUTTON_BACKGROUND);
  const [availableFonts, setAvailableFonts] = useState([]);

  const { appConfig, fonts } = useUserContext();

  const history = useHistory();
  const location = useLocation();

  const openCallToAction = useCallback(
    (pushHistory = true) => {
      if (!isCallToActionOpened) {
        setIsCallToActionOpened(true);
        pushHistory &&
          history.push({
            pathname: location.pathname,
            search: 'cta=true',
          });
      }
    },
    [isCallToActionOpened, history, location]
  );

  const closeCallToAction = useCallback((popHistory = true) => {
    setIsCallToActionOpened(false);
    resetCallActions();
    if (popHistory) {
      if (history.length > 2) {
        history.goBack();
      } else {
        // Handle cases when page with search parameter was loaded
        history.replace({
          pathname: location.pathname,
          search: '',
        });
      }
    }
  }, []);

  const updateInitialCallActions = useCallback(actions => {
    setInitialCallActions(actions);
  }, []);

  const updateCallActionsRangeChanged = useCallback(value => {
    setCallActionRangeChanged(value);
  }, []);

  // add new actions
  const updateCallActions = useCallback(actions => {
    setCallActions(actions);
  }, []);

  // change existing actions
  const changeCallActions = useCallback((id, changes) => {
    setCallActions(prevState =>
      prevState.map(action => {
        if (action.id === id) {
          return { ...action, ...changes };
        }

        return action;
      })
    );
  }, []);

  const changeSelectedCallAction = useCallback(action => {
    if (action && typeof action == 'object' && Object.keys(action).length === 0) {
      setIsNewCallToAction(false);
    }

    setSelectedCallAction(action);
  }, []);

  const addNewCallAction = useCallback(newAction => {
    setCallActions(prevState => [...prevState, newAction].sort((a, b) => a.offset - b.offset));
    changeSelectedCallAction(newAction);
  }, []);

  const removeCallAction = useCallback(actionId => {
    setCallActions(prevState => prevState.filter(action => action.id !== actionId));
    changeSelectedCallAction({});
  }, []);

  const updateCallActionsSeekValue = useCallback(state => {
    if (typeof state === 'number') setCallActionsSeekValue(state);
  }, []);

  const callActionChanged = useCallback(value => {
    setIsCallActionsChanged(value);
  }, []);

  const changeIsNewActionTimeline = useCallback(value => {
    setIsNewActionTimeline(value);
  }, []);

  const resetCallActions = useCallback(() => {
    setCallActions(null);
    setInitialCallActions([]);
  }, []);

  useEffect(() => {
    let namecardFonts = [];
    if (appConfig?.name_card) {
      let textColors = [];
      let buttonColors = [];

      if (appConfig.name_card.type === 'ONE_PANEL') {
        const panel = appConfig.name_card?.label_top;
        const backgroundColor = appConfig.name_card?.background_color;
        textColors = [panel?.font_color];
        buttonColors = [backgroundColor];
        namecardFonts = [panel?.font_name];
      } else {
        const panelTop = appConfig.name_card?.panel_top;
        const panelBottom = appConfig.name_card?.panel_bottom;
        textColors = [panelBottom?.font_color, panelTop?.font_color];
        buttonColors = [panelBottom?.background_color, panelTop?.background_color];
        namecardFonts = [panelBottom?.font_name, panelTop?.font_name];
      }

      textColors = textColors
        .filter((color, index) => color && textColors.indexOf(color) === index)
        .map((color, index) => ({ id: index, name: RGBAToHexA(color) }));
      buttonColors = buttonColors
        .filter((color, index) => color && buttonColors.indexOf(color) === index)
        .map((color, index) => ({ id: index, name: RGBAToHexA(color) }));

      if (textColors.length) {
        setAvailableTextColors(textColors);
      }

      if (buttonColors.length) {
        setAvailableButtonColors(buttonColors);
      }
    }

    if (fonts) {
      let allFonts = fonts.filter(font => !font.default);
      if (allFonts.length === 0) {
        allFonts = fonts;
      }

      // Add namecard fonts to available fonts if they exist and aren't already included
      if (namecardFonts?.length) {
        const namecardFontObjects = namecardFonts
          .filter(font => font && !allFonts.find(f => f.name === font)).map(fontName => appConfig.fonts.find(item => item.name === fontName));

        allFonts = [...allFonts, ...namecardFontObjects];
      }

      setAvailableFonts(allFonts.map(item => ({ ...item, id: item.font_id ?? item.id })));
    }
  }, [appConfig, fonts]);

  return useMemo(
    () => ({
      isCallToActionOpened,
      callActions,
      initialCallActions,
      callActionsSeekValue,
      selectedCallAction,
      isCallActionsChanged,
      isNewActionTimeline,
      callActionRangeChanged,
      setCallActions,
      openCallToAction,
      closeCallToAction,
      changeCallActions,
      updateCallActions,
      updateInitialCallActions,
      updateCallActionsSeekValue,
      changeSelectedCallAction,
      addNewCallAction,
      removeCallAction,
      callActionChanged,
      updateCallActionsRangeChanged,
      changeIsNewActionTimeline,
      resetCallActions,
      isNewCallToAction,
      setIsNewCallToAction,
      availableTextColors,
      availableButtonColors,
      availableFonts,
    }),
    [
      isCallToActionOpened,
      callActions,
      callActionRangeChanged,
      initialCallActions,
      callActionsSeekValue,
      selectedCallAction,
      isCallActionsChanged,
      isNewActionTimeline,
      openCallToAction,
      closeCallToAction,
      changeCallActions,
      updateCallActions,
      updateInitialCallActions,
      updateCallActionsSeekValue,
      changeSelectedCallAction,
      addNewCallAction,
      removeCallAction,
      callActionChanged,
      updateCallActionsRangeChanged,
      changeIsNewActionTimeline,
      resetCallActions,
      isNewCallToAction,
      setIsNewCallToAction,
      availableTextColors,
      availableButtonColors,
      availableFonts,
    ]
  );
}

export const useCallToActionContext = constate(useCallToAction);

const CallToActionContext = {
  Provider: useCallToActionContext.Provider,
};

export default CallToActionContext;
